import React, { useRef, useState } from "react";
import * as PR from "../../prime-modules/index";
import { exportIcon } from "../../assets/images";
import "./index.scss";
import { useQuery } from "react-query";
import { afDashboard } from "../../services/affiliateApi";
import { hasAnyPermission, hasPermission, modifyDate, PERMISSIONS, setPrice, VOUCHER_MANAGEMENT_MASK } from "../../utils/reuse";
import moment from "moment";
import { cloneDeep, isEqual } from 'lodash';
import { handleExportCSV } from "./helpers";
import Layout from "../../components/layout/Layout";
import { useSelector } from "react-redux";
import PrepaidVouchers from "./PrepaidVouchers";
import { globalConfig } from "../../constants";

const defaultFilter = { listingType: 'list', offset: 0, limit: globalConfig.pageCount + 1, sortField: 'createdTs', sortOrder: 'desc' };

const AffiliatesDashboard = () => {
    const appName = process.env.REACT_APP_NAME;
    const isCorporate = appName?.includes("corporate");
    const { affiliate } = useSelector(state => state.language?.activelang);
    const { afUserData } = useSelector((state) => state.afAuth);
    const userRolePermission = afUserData?.permission;
    const showVoucherManagementTab = hasAnyPermission(userRolePermission, VOUCHER_MANAGEMENT_MASK);
    const showOrderManagementTab = hasPermission(userRolePermission, PERMISSIONS.SEE_ORDERS);
    const showUsersManagement = hasPermission(userRolePermission, PERMISSIONS.SEE_USERS);

    const [dates, setDates] = useState(null);
    const params = useRef({ ...defaultFilter });
    const [page, setPage] = useState(1);
    const [activeTabIndex, setActiveTabIndex] = useState(showOrderManagementTab ? 0 : 1);
    const toast = useRef(null);
    const searchedParams = useRef({ ...defaultFilter });


    const { data, refetch, isLoading } = useQuery(
        ['AFFILIATE_DASHBOARD', afUserData?.afId],
        () => afDashboard(`orders?listingType=${params.current.listingType ?? ''}&offset=${params.current.offset ?? ''}&limit=${params.current.limit ?? ''}&orderBy=${params.current.sortField ?? ''}&direction=${params.current.sortOrder ?? ''}&orderStatus=COMPLETED&purchaseDateFrom=${params.current.purchaseDateFrom ?? ''}&purchaseDateTo=${params.current.purchaseDateTo !== "" ? params.current.purchaseDateTo ?? "" : params.current.purchaseDateFrom ?? ""}`),
        {
            retry: 1,
            enabled: showOrderManagementTab,
            staleTime: 10000 * 50,
            onSuccess: o => {
                if (params.current.listingType === 'export') {
                    params.current.listingType = 'list';
                    if (o !== null) {
                        const ex = cloneDeep(o);
                        ex?.forEach(el => {
                            el.createdTs = modifyDate(el.createdTs)
                            el.totalOrderAmount = setPrice(isCorporate ? el?.charge : el?.payout ?? 0);
                            if (el.user) {
                                el.userDetails = `${el?.user?.firstName + el?.user?.lastName || ''} (${el?.user?.email || ''})`;
                            } else {
                                el.userDetails = '';
                            }
                        });
                        handleExportCSV(ex, showOrderManagementTab);
                    }
                }
                searchedParams.current = { ...params.current }
            },
            onError: o => { toast.current.show({ severity: 'error', life: globalConfig?.errorToastDisplayTime, summary: 'Error', detail: o?.message }) }
        }
    );

    const dateChangeHandler = ({ value }) => {
        if (value?.length) {
            const [from, to] = value;
            params.current.purchaseDateFrom = from ? moment(from).format('MM-DD-YYYY') : '';
            params.current.purchaseDateTo = to ? moment(to).format('MM-DD-YYYY') : '';
            setDates([from, to]);
        } else {
            params.current.purchaseDateFrom = "";
            params.current.purchaseDateTo = "";
            setDates(null);
        }
        refetch();
    };

    const resetClickHandler = () => {
        setDates(null)
        setPage(1)
        searchedParams.current = { ...defaultFilter }
        params.current = { ...defaultFilter };
        refetch();
    }

    const paginationClickHandler = v => {
        params.current.offset = ((page + v) - 1) * globalConfig.pageCount;
        setPage(p => p + v);
        refetch();
    }

    const sortClickHandler = k => {
        params.current.sortField = k;
        params.current.offset = 0;
        setPage(1);
        params.current.sortOrder = params.current.sortOrder === 'asc' ? 'desc' : 'asc';
        refetch()
    }

    const downloadCsvClickHandler = () => {
        params.current.listingType = 'export';
        refetch();
    }

    const header = (
        <div className="flex flex-wrap align-items-center justify-content-end gap-2">
            <div className="filter-block flex flex-wrap align-items-center gap-3">
                <div className="relative">
                    <PR.Calendar value={dates} dateFormat="dd-mm-yy" onChange={dateChangeHandler} showButtonBar={isEqual(searchedParams.current, defaultFilter)} selectionMode="range" readOnlyInput hideOnRangeSelection placeholder={affiliate?.AFFNL0048} showIcon iconPos="left" className="purchased-date" maxDate={new Date()} />
                    {dates !== null && <i className="pi pi-times reset-icon" title="Reset" onClick={resetClickHandler}></i>}
                </div>
                <PR.Button onClick={downloadCsvClickHandler} disabled={data?.length < 1} className="filter-buttons export">
                    <PR.Image src={exportIcon} alt="Export CSV" className="export-icon" />
                    <span>{affiliate?.AFFNL0047}</span>
                </PR.Button>
            </div>
        </div>
    );
    const tableFooter = <div className={isLoading ? "pagination-section btn-disable" : 'pagination-section'}>
        <PR.Button icon="pi pi-chevron-left" onClick={paginationClickHandler.bind(null, -1)} disabled={page <= 1} />
        <p>{page}</p>
        <PR.Button icon="pi pi-chevron-right" onClick={paginationClickHandler.bind(null, 1)} disabled={data?.length <= (globalConfig.pageCount)} />
    </div>

    const orderStatus = ({ orderStatus }) => <span className={`status ${orderStatus === 'COMPLETED' ? 'completed' : orderStatus === 'PENDING' ? "pending" : 'failed'}`}>
        {orderStatus}
    </span>

    const sortSymbol = k => params.current.sortField === k ? `-amount-${params.current.sortOrder === 'asc' ? 'up' : 'down'}` : '';

    const copyOrderID = (orderID) => {
        navigator.clipboard.writeText(orderID);
        toast.current.show({
            severity: 'success',
            summary: 'Copied',
            detail: `Order ID copied to clipboard!`,
            life: globalConfig?.toastDisplayTime,
        });
    };

    const copyReferenceID = (referenceID) => {
        navigator.clipboard.writeText(referenceID);
        toast.current.show({
            severity: 'success',
            summary: 'Copied',
            detail: `Reference ID copied to clipboard!`,
            life: globalConfig?.toastDisplayTime,
        });
    };

    const getHeaderStyle = (field) => ({
        backgroundColor: params.current.sortField === field ? '#aba5a5' : 'transparent',
        color: params.current.sortField === field ? '#000' : 'inherit',
        cursor: "pointer",
    });

    return (
        <>
            <PR.Toast ref={toast} />
            <Layout>
                <section className="affiliates-dashboard">
                    <div className="affiliates-datatable-wrapper">
                        <div className="grid">
                            <div className={`col-12 p-0 ${(showOrderManagementTab && showVoucherManagementTab) ? '' : 'hide-tabs'}`}>
                                {showOrderManagementTab && !showVoucherManagementTab && (
                                    <h2>{affiliate?.AFFNL0020 || "Order Details"}</h2>
                                )}
                                {!showOrderManagementTab && showVoucherManagementTab && (
                                    <h2>{affiliate?.AFFNL0125 || "Prepaid Vouchers"}</h2>
                                )}
                                <PR.TabView className="affiliate-tabs" activeIndex={activeTabIndex}
                                    onTabChange={(e) => {
                                        setActiveTabIndex(e.index);
                                        if (e.index === 0) {
                                            refetch();
                                        }
                                    }}>
                                    {showOrderManagementTab &&
                                        <PR.TabPanel header={affiliate?.AFFNL0124 || "Orders"}>
                                            <PR.DataTable sortField="" footer={!!data ? tableFooter : null} value={data ?? []} header={header} className="sales-datatable" emptyMessage="No data available.">
                                                {showUsersManagement ? <PR.Column
                                                    field="userDetails"
                                                    style={{ width: "20%" }}
                                                    header={<div onClick={sortClickHandler.bind(null, 'userDetails')} style={getHeaderStyle('userDetails')}>{affiliate?.AFFNL0066 || "User details"} <i className={`pi pi-sort${sortSymbol('userDetails')}-alt`}></i> </div>}
                                                    body={(rowData) =>
                                                        isLoading ? (
                                                            <PR.Skeleton width="100%" height="22px" />
                                                        ) : (
                                                            <div className="user">
                                                                <p>{rowData?.user?.firstName + " " + rowData?.user?.lastName}</p>
                                                                <p className="email">{rowData?.user?.email}</p>
                                                            </div>
                                                        )
                                                    }
                                                /> : null}
                                                <PR.Column
                                                    field="dataPlan"
                                                    style={{ width: "16%" }}
                                                    body={isLoading && <PR.Skeleton width="100%" height="22px" ></PR.Skeleton>}
                                                    header={<div onClick={sortClickHandler.bind(null, 'dataPlan')} style={getHeaderStyle('dataPlan')}>{affiliate?.AFFNL0055} <i className={`pi pi-sort${sortSymbol('dataPlan')}-alt`}></i> </div>}
                                                />
                                                <PR.Column
                                                    field="createdTs"
                                                    style={{ width: "12%" }}
                                                    header={<div onClick={sortClickHandler.bind(null, 'createdTs')} style={getHeaderStyle('createdTs')}>{affiliate?.AFFNL0056} <i className={`pi pi-sort${sortSymbol('createdTs')}-alt`}></i></div>}
                                                    body={isLoading ? <PR.Skeleton width="100%" height="22px" ></PR.Skeleton> : d => <>{modifyDate(d.createdTs)}</>}
                                                />
                                                <PR.Column
                                                    field={isCorporate ? "charge" : "payout"}
                                                    style={{ width: "8%" }}
                                                    header={<div onClick={sortClickHandler.bind(null, isCorporate ? 'charge' : 'payout')} style={getHeaderStyle('totalOrderAmount')}>
                                                        {(isCorporate ? affiliate?.AFFNL0153 : affiliate?.AFFNL0154) || 'Payout'}
                                                        <i className={`pi pi-sort${sortSymbol(isCorporate ? 'charge' : 'payout')}-alt`}></i></div>}
                                                    body={isLoading ? <PR.Skeleton width="100%" height="22px" ></PR.Skeleton> : d => <>{setPrice(isCorporate ? d?.charge : d?.payout ?? 0)}</>}
                                                />
                                                <PR.Column
                                                    field="orderStatus"
                                                    style={{ width: "11%" }}
                                                    header={<div onClick={sortClickHandler.bind(null, 'orderStatus')} style={getHeaderStyle('orderStatus')}>{affiliate?.AFFNL0058} <i className={`pi pi-sort${sortSymbol('orderStatus')}-alt`}></i></div>}
                                                    body={isLoading ? <PR.Skeleton width="100%" height="22px" ></PR.Skeleton> : orderStatus}
                                                />
                                                <PR.Column
                                                    field="orderId"
                                                    style={{ width: "20%" }}
                                                    header={<div onClick={sortClickHandler.bind(null, 'orderId')} style={getHeaderStyle('orderId')}>{affiliate?.AFFNL0062 || "Order ID"} <i className={`pi pi-sort${sortSymbol('orderId')}-alt`}></i></div>}
                                                    body={(rowData) =>
                                                        isLoading ? (
                                                            <PR.Skeleton width="100%" height="22px" />
                                                        ) : (
                                                            <div className="flex align-items-center justify-content-between">
                                                                <span className="order-id">{rowData.orderId}</span>
                                                                <i
                                                                    title="Copy Order ID"
                                                                    className="pi pi-copy copy-icon"
                                                                    onClick={() => copyOrderID(rowData.orderId)}
                                                                    style={{ marginLeft: "5px", cursor: "pointer" }}
                                                                />
                                                            </div>
                                                        )
                                                    }
                                                />
                                                <PR.Column
                                                    field="externalReference"
                                                    style={{ width: "12%" }}
                                                    header={<div onClick={sortClickHandler.bind(null, 'externalReference')} style={getHeaderStyle('externalReference')}>{affiliate?.AFFNL0063 || "Reference ID"} <i className={`pi pi-sort${sortSymbol('externalReference')}-alt`}></i></div>}
                                                    body={(rowData) =>
                                                        isLoading ? (
                                                            <PR.Skeleton width="100%" height="22px" />
                                                        ) : (
                                                            <div className="flex align-items-center justify-content-between">
                                                                <span style={{ width: "88%" }}>{rowData.externalReference ? rowData.externalReference : "-"}</span>
                                                                {rowData.externalReference ?
                                                                    <i
                                                                        title="Copy Reference ID"
                                                                        className="pi pi-copy copy-icon"
                                                                        onClick={() => copyReferenceID(rowData.externalReference)}
                                                                        style={{ marginLeft: "5px", cursor: "pointer" }}
                                                                    />
                                                                    : null}
                                                            </div>
                                                        )
                                                    }
                                                />
                                            </PR.DataTable>
                                        </PR.TabPanel>
                                    }
                                    {showVoucherManagementTab &&
                                        <PR.TabPanel header={affiliate?.AFFNL0125 || "Prepaid Vouchers"}>
                                            <PrepaidVouchers />
                                        </PR.TabPanel>
                                    }
                                </PR.TabView>
                            </div>
                        </div>
                    </div>
                </section>
            </Layout>
        </>
    );
};

export default AffiliatesDashboard;